import { noop } from 'lodash'
import {
  ModalConfirmV2,
  ModalSizeVariants,
  Truncate,
  useAuth,
} from '@labrav/react-components'
import { OfferStakeholders } from '../../../types/flyout'
import { useStyles } from './ViewStakeholders.styles'
import { DetailRow } from '../DetailRow/DetailRow'
import { RecipentStakeholderRow } from '../RecipientStakeholderRow/RecipentStakeholderRow'
import { SectionWrapper } from '../SectionWrapper/SectionWrapper'
import { faPen, faShelves, faUser } from '@fortawesome/pro-regular-svg-icons'
import { Dispatch, SetStateAction, useMemo } from 'react'
import clsx from 'clsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { EditStakeholders } from '../EditStakeholders/EditStakeholders'
import {
  ViewValueWithTruncate,
  getSafeString,
  useCurrentCloudProvider,
} from '@labrav/common'
import { providerToAuthCloud, CloudAccountProvider } from '@labrav/common'
import { useTheme } from '@mui/material/styles'
import { StakeholderUserOption } from './stakeholderUserOption'

export type { StakeholderUserOption } from './stakeholderUserOption'

export const ViewStakeholders = ({
  offerStakeholders,
  customOfferStakeholders,
  isStakeholderEditActionDisabled,
  addStakeholderToolTipMessage,
  hideEditActionStakeholder,
  handleSubmitStakeholder,
  setOpenEditStakeholders,
  openEditStakeHolders,
  isUpdateLoading,
  hideStakeholders,
  users = [],
}: {
  offerStakeholders: OfferStakeholders[]
  customOfferStakeholders: OfferStakeholders[]
  isStakeholderEditActionDisabled?: boolean
  addStakeholderToolTipMessage?: string
  hideEditActionStakeholder?: boolean
  handleSubmitStakeholder?: (
    offerStakeholders: OfferStakeholders[],
    customOfferStakeholders: OfferStakeholders[]
  ) => void
  setOpenEditStakeholders: Dispatch<SetStateAction<boolean>>
  openEditStakeHolders: boolean
  isUpdateLoading?: boolean
  hideStakeholders?: boolean
  users?: StakeholderUserOption[]
}) => {
  const { classes } = useStyles()
  const theme = useTheme()
  const { loading: authLoading, canDo } = useAuth()
  const cloud = useCurrentCloudProvider()

  const canWriteMarketplace = useMemo(() => {
    return (
      !authLoading &&
      canDo({
        cloud: providerToAuthCloud(cloud.toLowerCase() as CloudAccountProvider),
        service: 'marketplace',
        action: 'create',
      })
    )
  }, [authLoading, canDo, cloud])

  const permissionMessageString =
    'This action is disabled because you have read-only access'

  const editAction = (
    <div
      className={clsx(
        classes.editActionLabel,
        isStakeholderEditActionDisabled ? classes.disabledEdit : ''
      )}
      data-testid={'edit-action'}
    >
      <FontAwesomeIcon
        icon={faPen}
        style={{
          width: '14px',
          height: '14px',
          fontSize: '14px',
          padding: '0px 9px 0px 0px',
          color: theme.palette.primary.main,
          opacity: isStakeholderEditActionDisabled ? '39%' : '100%',
        }}
      />
      <div>Edit</div>{' '}
    </div>
  )

  const emailsAndNames = users.map(user => ({
    email: getSafeString(user.email),
    name: user?.name.trim() ? user.name : getSafeString(user.email),
  }))

  const labraEmails = users.map(user => getSafeString(user.email))

  const commonEmails = offerStakeholders
    .map(stakeholder => getSafeString(stakeholder.email))
    .filter(email => emailsAndNames.some(user => user.email === email))
    .map(email => {
      const user = emailsAndNames.find(user => user.email === email)
      if (user) {
        const name = user.name
        const [firstName, lastName] = name?.split(' ') || []
        return { email, firstName, lastName, name }
      }
      return {
        email,
        firstName: undefined,
        lastName: undefined,
        name: undefined,
      }
    })

  const totalStakeholders =
    offerStakeholders.length + customOfferStakeholders.length

  return (
    <>
      <SectionWrapper
        heading="Internal stakeholders"
        icon={faUser}
        className={classes.rightSection}
        handleAction={() => setOpenEditStakeholders(true)}
        actionDisabled={!canWriteMarketplace || isStakeholderEditActionDisabled}
        actionDisabledTooltip={
          !canWriteMarketplace
            ? permissionMessageString
            : isStakeholderEditActionDisabled
              ? addStakeholderToolTipMessage
              : ''
        }
        varient="small"
        {...(!hideEditActionStakeholder ? { actionLabel: editAction } : {})}
        children={
          <div
            className={clsx(
              classes.section,
              !totalStakeholders && classes.emptySection
            )}
            data-testid={'offer-stakeholder-view'}
          >
            {commonEmails?.map((item, index) => {
              const isLastRow =
                customOfferStakeholders.length >= 1
                  ? false
                  : index === offerStakeholders.length - 1
              return (
                <DetailRow
                  borderBottom={
                    customOfferStakeholders.length >= 1
                      ? index !== offerStakeholders.length
                      : index !== offerStakeholders.length - 1
                  }
                  className={clsx(
                    classes.rowItem,
                    isLastRow && classes.lastRowItem
                  )}
                  children={
                    <RecipentStakeholderRow
                      title={
                        <div className={classes.stakeholderTitle}>
                          <ViewValueWithTruncate
                            value={`${item?.firstName || ''} ${item?.lastName || ''}`.trim()}
                          />
                        </div>
                      }
                      value={
                        <Truncate
                          width={'90%'}
                          clipboardTooltip
                          showIcon={false}
                        >
                          {getSafeString(item?.email)}
                        </Truncate>
                      }
                      keyName={'stakeholders'}
                    />
                  }
                />
              )
            })}

            {customOfferStakeholders?.map((item, index) => {
              return (
                <DetailRow
                  borderBottom={index !== customOfferStakeholders.length - 1}
                  className={clsx(
                    classes.rowItem,
                    index === customOfferStakeholders.length - 1 &&
                      classes.lastRowItem
                  )}
                  children={
                    <RecipentStakeholderRow
                      value={
                        <Truncate
                          width={'90%'}
                          clipboardTooltip
                          showIcon={false}
                        >
                          {getSafeString(item?.email)}
                        </Truncate>
                      }
                      keyName={'stakeholders'}
                    />
                  }
                />
              )
            })}
          </div>
        }
      />
      <ModalConfirmV2
        title={'Edit Stakeholders'}
        content={
          <EditStakeholders
            offerStakeholders={(offerStakeholders || []).filter(item =>
              labraEmails.includes(getSafeString(item?.email))
            )}
            customOfferStakeholders={(customOfferStakeholders || []).filter(
              item => !labraEmails.includes(getSafeString(item?.email))
            )}
            handleCloseStakeholders={() => setOpenEditStakeholders(false)}
            handleSubmit={handleSubmitStakeholder}
            isUpdateLoading={isUpdateLoading}
            hideStakeholders={hideStakeholders}
            users={users}
          />
        }
        hideCancelButton={true}
        open={openEditStakeHolders}
        hideAcceptButton={true}
        onAccept={noop}
        onDecline={() => setOpenEditStakeholders(false)}
        variant={ModalSizeVariants.large}
        icon={<FontAwesomeIcon style={{ height: '14px' }} icon={faShelves} />}
      />
    </>
  )
}
